import { assign, createMachine } from 'xstate';
import { OmitStrict } from '@tink/core/src/types';
import { ApplicationError } from '../../components/ApplicationErrorBoundary/errors/ApplicationError';
import { AccountDialogType, GeneratedReport, ReportAccount } from '../../reports/models/report';
import { BundledFlowParameters } from '../../reports/services/reports-client';

import {
  GenerateReportsPayload,
  ReportsService,
  reportsService,
} from '../../reports/services/reports';

type PayloadProps = {
  getAccountsPayload: BundledFlowParameters;
  generateReportsPayload: OmitStrict<GenerateReportsPayload, 'accounts'>;
};

export type MachineProps = {
  accountDialogType: AccountDialogType;
} & PayloadProps;

type Services = {
  getAccounts: { data: ReportAccount[] };
  generateReports: { data: GeneratedReport[] };
};

type Context = {
  accountsResponse: ReportAccount[] | undefined;
  selectedAccountIds: Array<ReportAccount['id']>;
  createReportResponse: GeneratedReport[] | undefined;
  error: ApplicationError | undefined;
  idempotencyKey: string;
};

export type Events =
  | { type: 'EVENT_ACCOUNTS_SELECTED'; data: Context['selectedAccountIds'] }
  | { type: 'CREDENTIALS' }
  | { type: 'SET_IDEMPOTENCY_KEY'; data: Context['idempotencyKey'] };

export const createAccountCheckMachine = (
  props: MachineProps,
  services: ReportsService = reportsService
) =>
  createMachine(
    {
      predictableActionArguments: true,
      context: {
        accountsResponse: undefined,
        selectedAccountIds: [],
        createReportResponse: undefined,
        error: undefined,
        idempotencyKey: '',
      },
      tsTypes: {} as import('./accountCheckMachine.typegen').Typegen0,
      schema: { events: {} as Events, context: {} as Context, services: {} as Services },
      id: 'accountCheckMachine',
      initial: 'auth',
      states: {
        auth: {
          on: {
            CREDENTIALS: {
              target: 'GET_ACCOUNTS',
            },
            SET_IDEMPOTENCY_KEY: {
              actions: 'set_idempotency_key',
            },
          },
        },
        GET_ACCOUNTS: {
          invoke: {
            src: 'getAccounts',
            onDone: [
              {
                actions: 'set_getAccounts_response',
                target: 'GET_ACCOUNTS_DONE',
              },
            ],
            onError: [
              {
                actions: 'set_error',
                target: 'ERROR',
              },
            ],
          },
        },
        GET_ACCOUNTS_DONE: {
          always: [
            {
              cond: 'check_if_more_than_one_account',
              target: 'CHECK_ACCOUNT_SELECTION',
            },
            {
              actions: 'select_all_accounts',
              target: 'CREATE_REPORT',
            },
          ],
        },
        CHECK_ACCOUNT_SELECTION: {
          always: [
            {
              cond: 'check_if_multi_account_mode',
              target: 'MULTIPLE_ACCOUNT_SELECTION_VIEW',
            },
            {
              cond: 'check_if_single_account_mode',
              target: 'SINGLE_ACCOUNT_SELECTION_VIEW',
            },
            {
              actions: 'select_all_accounts',
              target: 'CREATE_REPORT',
            },
          ],
        },
        SINGLE_ACCOUNT_SELECTION_VIEW: {
          on: {
            EVENT_ACCOUNTS_SELECTED: {
              actions: 'set_selectedAccounts',
              target: 'CREATE_REPORT',
            },
          },
        },
        MULTIPLE_ACCOUNT_SELECTION_VIEW: {
          on: {
            EVENT_ACCOUNTS_SELECTED: {
              actions: 'set_selectedAccounts',
              target: 'CREATE_REPORT',
            },
          },
        },
        CREATE_REPORT: {
          invoke: {
            src: 'generateReports',
            onDone: [
              {
                actions: 'set_generateReport_response',
                target: 'SUCCESS',
              },
            ],
            onError: [
              {
                actions: 'set_error',
                target: 'ERROR',
              },
            ],
          },
        },
        SUCCESS: {
          type: 'final',
        },
        ERROR: {
          type: 'final',
        },
      },
    },
    {
      services: {
        getAccounts: () => services.getAccounts(props.getAccountsPayload),
        generateReports: (context) => {
          return services.generateReports(
            {
              accounts: context.selectedAccountIds,
              ...props.generateReportsPayload,
            },
            context.idempotencyKey
          );
        },
      },
      actions: {
        set_error: assign({ error: (_, event) => event.data as ApplicationError }),
        set_getAccounts_response: assign({
          accountsResponse: (_, event) => event.data,
        }),
        select_all_accounts: assign({
          selectedAccountIds: (context) => context.accountsResponse!.map((account) => account.id),
        }),
        set_generateReport_response: assign({
          createReportResponse: (_, event) => event.data,
        }),
        set_selectedAccounts: assign({ selectedAccountIds: (_, event) => event.data }),
        set_idempotency_key: assign({
          idempotencyKey: (_, event) => event.data,
        }),
      },
      guards: {
        check_if_more_than_one_account: (context) => context.accountsResponse!.length > 1,
        check_if_multi_account_mode: () => props.accountDialogType === AccountDialogType.MULTI,
        check_if_single_account_mode: () => props.accountDialogType === AccountDialogType.SINGLE,
      },
    }
  );
